import * as React from "react"

/** The two ways a pointer scrolls a list without touching its scrollbar. */
const SCROLL_GESTURES = ["wheel", "touchmove"] as const

/**
 * Keeps a scrolling list usable inside a modal.
 *
 * A modal Dialog holds the page still by cancelling every scroll gesture that
 * did not start inside the dialog itself, and a menu rendered through a
 * portal starts outside it: its list stops scrolling for as long as the
 * dialog is open, leaving only search to reach the options further down.
 * Claiming the gesture on the scrolling element keeps it away from that
 * guard, so the list scrolls while the page behind the dialog still cannot.
 *
 * Attach the returned ref to the element that owns the overflow.
 */
export function useScrollLockExemption<
  T extends HTMLElement,
>(): React.RefCallback<T> {
  return React.useCallback((node: T | null) => {
    if (!node) {
      return
    }
    const claim = (event: Event) => event.stopPropagation()
    for (const gesture of SCROLL_GESTURES) {
      node.addEventListener(gesture, claim)
    }
    return () => {
      for (const gesture of SCROLL_GESTURES) {
        node.removeEventListener(gesture, claim)
      }
    }
  }, [])
}
